import React from "react";
import { siteConfig } from "../../config/siteConfig";
import Icon from "../ui/Icon";
import Eyebrow from "../ui/Eyebrow";
import SealChip from "../ui/SealChip";
import Skeleton from "../ui/Skeleton";
import useInView from "../../hooks/useInView";
import useCountUp from "../../hooks/useCountUp";

// Cellule du PROOF-LEDGER : numéral Fraunces tabular-nums avec count-up au scroll-in.
// La valeur finale (réelle) est rendue en texte dès le départ → 0 CLS, SEO-safe.
const ProofCell = ({ value, label, inView, sourceLabel }) => {
  const display = useCountUp(value, { inView });
  return (
    <div className="flex flex-col items-center px-4 py-5 text-center sm:py-6">
      <span className="font-display text-3xl font-semibold tabular-nums text-indigo-700 md:text-4xl">
        {display}
      </span>
      <span className="mt-1.5 text-sm text-slate-500">{label}</span>
      {sourceLabel ? (
        <SealChip label={sourceLabel} icon="document-check" className="mt-3" />
      ) : null}
    </div>
  );
};

const Hero = ({ onCtaClick, onDemoClick }) => {
  const { hero, stats, payment, demo, exam, seo } = siteConfig;
  const previewQ =
    demo && demo.questions && demo.questions[0] ? demo.questions[0] : null;
  const previewDomain = (seo && seo.domain) || "";

  // Référence officielle au programme (proof-as-layout). Fallback "" → rien rendu.
  const officialReference = siteConfig.officialReference || "";
  const sealLabels = siteConfig.sealLabels || {};
  const verifiedLabel = sealLabels.verified || "";
  const sourceLabel = sealLabels.source || "";

  // Observers partagés : un pour le proof-ledger (count-up), un pour le mockup
  // (barre de progression + révélation one-shot de la bonne réponse).
  const ledger = useInView({ threshold: 0.3 });
  const mockup = useInView({ threshold: 0.2 });

  // Ítems de tranquilidad: configurables por niche desde siteConfig.hero.reassuranceItems.
  // Si la niche no los define, se usa un valor por defecto que compone el texto del
  // pago con payment.providerName (en vez de codificar "Stripe" en el componente).
  const reassuranceItems =
    hero.reassuranceItems && hero.reassuranceItems.length > 0
      ? hero.reassuranceItems
      : [
          "Tillgång direkt",
          "Avsluta när du vill",
          `Säker betalning: ${payment.providerName}`,
        ];

  const totalQuestions = (exam && exam.simulatorQuestions) || 75;
  const progressPct = 16;

  return (
    <section className="bg-mesh grain relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <Reveal className="mb-8 inline-flex">
            <Eyebrow>{hero.badge}</Eyebrow>
          </Reveal>

          <Reveal index={1}>
            <h1 className="mb-6 break-words text-4xl font-extrabold leading-[1.05] tracking-tight text-balance sm:text-5xl md:text-6xl lg:text-7xl">
              <span className="text-slate-900">{hero.titleLine1}</span>
              <br />
              <span className="bg-gradient-to-r from-indigo-700 to-sky-600 bg-clip-text text-transparent">
                {hero.titleLine2}
              </span>
            </h1>
          </Reveal>

          {officialReference ? (
            <Reveal index={2} className="mb-8 flex flex-col items-center gap-3">
              <p className="text-sm font-semibold text-slate-500 md:text-base">
                {officialReference}
              </p>
              {verifiedLabel ? (
                <SealChip label={verifiedLabel} icon="seal" />
              ) : null}
            </Reveal>
          ) : null}

          <Reveal index={3}>
            <p className="mx-auto mb-10 max-w-3xl text-xl text-pretty text-slate-600 md:text-2xl">
              {hero.subtitle}
            </p>
          </Reveal>

          <Reveal
            index={4}
            className="flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <button
              onClick={onCtaClick}
              className="btn-primary glow-cta group px-8 py-4 text-lg"
            >
              <span>{hero.primaryCta}</span>
              <svg
                className="h-5 w-5 transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </button>
            <button
              onClick={onDemoClick}
              className="btn-ghost group px-8 py-4 text-lg"
            >
              <Icon name="spark" className="h-5 w-5 text-indigo-700" />
              <span>{hero.secondaryCta}</span>
              {hero.secondaryCtaBadge ? (
                <span
                  aria-hidden="true"
                  className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-bold text-indigo-700 ring-1 ring-inset ring-indigo-100"
                >
                  {hero.secondaryCtaBadge}
                </span>
              ) : null}
            </button>
          </Reveal>

          <Reveal
            index={5}
            className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
          >
            {reassuranceItems.map((item, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500"
              >
                <Icon name="check" className="h-4 w-4 text-indigo-700" />
                {item}
              </span>
            ))}
          </Reveal>

          {/* PROOF-LEDGER : remplace les 4 chiffres flottants par une bande scellée. */}
          <Reveal as="div" index={6} className="mt-16">
            <div
              ref={ledger.ref}
              className="elev-2 mx-auto grid max-w-4xl grid-cols-2 divide-x divide-y divide-slate-200/70 overflow-hidden rounded-2xl bg-white ring-1 ring-slate-900/5 sm:divide-y-0 md:grid-cols-4"
            >
              {stats.map((stat, idx) => (
                <ProofCell
                  key={idx}
                  value={stat.value}
                  label={stat.label}
                  inView={ledger.inView}
                  sourceLabel={idx === 0 ? sourceLabel : ""}
                />
              ))}
            </div>
          </Reveal>

          {/* Vista previa del producto (mockup, decorativo) */}
          <Reveal
            as="div"
            index={7}
            className="mx-auto mt-20 max-w-5xl"
            aria-hidden="true"
          >
            <div ref={mockup.ref} className="relative">
              <div className="card-plate relative overflow-hidden bg-white">
                <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  {verifiedLabel ? (
                    <SealChip
                      label={verifiedLabel}
                      icon="seal"
                      className="text-[10px]"
                    />
                  ) : null}
                  <div className="mx-auto ml-2 hidden max-w-xs flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-400 sm:flex">
                    <Icon
                      name="lock"
                      className="h-3.5 w-3.5 text-emerald-500"
                    />
                    {previewDomain}
                  </div>
                </div>

                <div className="grid text-left md:grid-cols-[1fr_260px]">
                  <div className="p-6 md:p-8">
                    <div className="mb-5 flex items-center justify-between">
                      <span className="rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-indigo-700">
                        {(exam && exam.simulatorTitle) || "Simulering"}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-sm font-bold tabular-nums text-slate-700">
                        <Icon name="clock" className="h-4 w-4 text-slate-400" />
                        01:42:30
                      </span>
                    </div>
                    <div className="mb-2 flex items-center justify-between text-xs font-bold tabular-nums text-slate-400">
                      <span>Fråga 12 / {totalQuestions}</span>
                      <span>{progressPct}%</span>
                    </div>
                    <div className="mb-6 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-indigo-600 to-sky-500 transition-[width] duration-1000 ease-out"
                        style={{
                          width: mockup.inView ? `${progressPct}%` : "0%",
                        }}
                      />
                    </div>

                    {previewQ ? (
                      <>
                        <p className="mb-5 min-h-[3.5rem] text-base font-bold text-slate-900 md:text-lg">
                          {previewQ.q}
                        </p>
                        <div className="space-y-3">
                          {previewQ.o.map((opt, i) => {
                            // Révélation one-shot : la bonne réponse n'apparaît qu'au scroll-in.
                            const correct = i === previewQ.c && mockup.inView;
                            return (
                              <div
                                key={i}
                                className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 transition-colors duration-500 ${
                                  correct
                                    ? "border-emerald-400 bg-emerald-50"
                                    : "border-slate-200 bg-white"
                                }`}
                              >
                                <span
                                  className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-black transition-colors duration-500 ${
                                    correct
                                      ? "bg-emerald-500 text-white"
                                      : "bg-slate-100 text-slate-500"
                                  }`}
                                >
                                  {String.fromCharCode(65 + i)}
                                </span>
                                <span className="text-sm font-semibold text-slate-700">
                                  {opt}
                                </span>
                                {correct ? (
                                  <Icon
                                    name="check"
                                    className="ml-auto h-5 w-5 flex-shrink-0 text-emerald-500"
                                  />
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      // Skeleton fallback : jamais de blanc quand demo.questions est vide.
                      <>
                        <Skeleton className="mb-5 h-5 w-3/4 rounded-md" />
                        <div className="space-y-3">
                          {[0, 1, 2, 3].map((i) => (
                            <Skeleton
                              key={i}
                              className="h-12 w-full rounded-xl"
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="hidden flex-col gap-4 border-l border-slate-200 bg-slate-50 p-6 md:flex">
                    <div className="text-xs font-black uppercase tracking-wider text-slate-400">
                      Övningsprov
                    </div>
                    {[
                      {
                        k: "Fråga",
                        v: String((exam && exam.simulatorQuestions) || 75),
                      },
                      {
                        k: "Tid",
                        v: `${
                          (exam && exam.simulatorDurationMinutes) || 120
                        } min`,
                      },
                      {
                        k: "För godkänt",
                        v: `${(exam && exam.passingScore) || 60}%`,
                      },
                    ].map((row, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between"
                      >
                        <span className="text-xs font-semibold text-slate-500">
                          {row.k}
                        </span>
                        <span className="text-sm font-black tabular-nums text-slate-900">
                          {row.v}
                        </span>
                      </div>
                    ))}
                    <div className="mt-1 h-px bg-slate-200" />
                    <div className="flex items-center gap-2 text-indigo-700">
                      <Icon name="book" className="h-5 w-5" />
                      <span className="text-xs font-bold">
                        Officiella kunskapskrav
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Plancher neutre : la plate « flotte » au-dessus d'un dégradé slate. */}
              <div className="absolute inset-x-8 -bottom-4 -z-10 h-12 rounded-2xl bg-gradient-to-b from-slate-200/60 to-transparent blur-md" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// Reveal local minimal : applique .reveal + stagger --i et bascule .is-in au scroll-in
// via le hook partagé useInView. (Sous reduced-motion, inView=true → visible d'emblée.)
const Reveal = ({
  as = "div",
  index = 0,
  className = "",
  children,
  ...rest
}) => {
  const Tag = as;
  const { ref, inView } = useInView({ threshold: 0.15 });
  return (
    <Tag
      ref={ref}
      style={{ "--i": index }}
      className={`reveal${inView ? " is-in" : ""}${
        className ? ` ${className}` : ""
      }`}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Hero;
